import { KARAOKE_TRACK_NUMBER } from '../../constants';
import { Track } from '../Track';
import { KaraokeClip } from './KaraokeClip';

import type { CaptionSetKind } from '../../dubbing/heardDub';
import type { TranscriptToken } from '../../types';
import type { DubRowRecord, KaraokeData } from '../../types/karaoke';
import type { TextElement } from '../elements/TextElement';
import type { CaptionLineBreak } from './CaptionLineBreak';

/** The source-timed caption set: today's rows and their break records. */
export interface SourceCaptionSet {
  clips: KaraokeClip[];
  syntheticBreaks: CaptionLineBreak[];
}

export class KaraokeTrack extends Track {
  private _karaokeClips: KaraokeClip[];
  private _syntheticBreaks: CaptionLineBreak[];
  /**
   * PLAT-11905 — a dubbed language has two caption sets, and both live HERE, on
   * the track, so every way of removing the track (Clear captions, Captions
   * Off, a template strip, a produce) removes both, and an undo that puts the
   * track back puts both back.
   *
   * `liveKind` says which set the track shows. While the dub-timed set is
   * shown, the source-timed set waits in `sourceParked`. `dubRecord` is the
   * dub-timed set's row record, whichever set is shown.
   */
  private _liveKind: CaptionSetKind = 'source';
  private _sourceParked: SourceCaptionSet | null = null;
  private _dubRecord: DubRowRecord | null = null;

  constructor(
    props: Partial<KaraokeTrack & Track> & {
      syntheticBreaks?: CaptionLineBreak[];
    } = {},
  ) {
    super(props);
    this._karaokeClips = props.karaokeClips ? [...props.karaokeClips] : [];
    this._syntheticBreaks = props.syntheticBreaks
      ? [...props.syntheticBreaks]
      : [];
  }

  /**
   * Elements in the track as text elements
   */
  override get elements(): TextElement[] {
    return this._karaokeClips;
  }

  /**
   * Karaoke clips in the track
   */
  get karaokeClips(): KaraokeClip[] {
    return this._karaokeClips;
  }

  set karaokeClips(clips: KaraokeClip[]) {
    this._karaokeClips = clips;
  }

  /**
   * PLAT-9623: user-added line breaks inside translated caption phrases.
   * Track-level list (not per-clip) so a single record cleanly identifies
   * the split-point regardless of how the surrounding clips have been
   * reshuffled. Cleared whenever the track is regenerated.
   */
  get syntheticBreaks(): CaptionLineBreak[] {
    return this._syntheticBreaks;
  }

  set syntheticBreaks(breaks: CaptionLineBreak[]) {
    this._syntheticBreaks = breaks;
  }

  /** Which caption set the track shows. */
  get liveKind(): CaptionSetKind {
    return this._liveKind;
  }

  /** The source-timed set while the dub-timed set is shown, else `null`. */
  get sourceParked(): SourceCaptionSet | null {
    return this._sourceParked;
  }

  /** The dub-timed set's row record, or `null` before it is first built. */
  get dubRecord(): DubRowRecord | null {
    return this._dubRecord;
  }

  set dubRecord(record: DubRowRecord | null) {
    this._dubRecord = record;
  }

  /** The source-timed set, wherever it is — shown or parked. */
  get sourceSet(): SourceCaptionSet {
    if (this._liveKind === 'dub' && this._sourceParked) {
      return this._sourceParked;
    }
    return {
      clips: this._karaokeClips,
      syntheticBreaks: this._syntheticBreaks,
    };
  }

  /** Write the source-timed set back to wherever it is — shown or parked. */
  set sourceSet(set: SourceCaptionSet) {
    if (this._liveKind === 'dub') {
      this._sourceParked = {
        clips: set.clips,
        syntheticBreaks: set.syntheticBreaks,
      };
      return;
    }
    this._karaokeClips = set.clips;
    this._syntheticBreaks = set.syntheticBreaks;
  }

  /**
   * Show the dub-timed set's captions. The first time, the source-timed set is
   * parked, break records included (the dub-timed set has none of its own).
   */
  showDubSet(clips: KaraokeClip[]): void {
    if (this._liveKind === 'source') {
      this._sourceParked = {
        clips: this._karaokeClips,
        syntheticBreaks: this._syntheticBreaks,
      };
      this._syntheticBreaks = [];
      this._liveKind = 'dub';
    }
    this._karaokeClips = clips;
  }

  /** Show the source-timed set again, exactly as it was parked. */
  showSourceSet(): void {
    if (this._liveKind === 'source') return;
    const parked = this._sourceParked ?? { clips: [], syntheticBreaks: [] };
    this._karaokeClips = parked.clips;
    this._syntheticBreaks = parked.syntheticBreaks;
    this._sourceParked = null;
    this._liveKind = 'source';
  }

  /**
   * Creates a KaraokeTrack from karaoke data and transcript tokens
   * Optimized for large token arrays (~10,000 elements) using O(1) token lookup
   *
   * @param karaokeData - The karaoke data containing clips and configurations
   * @param transcript - Full array of transcript tokens (used for efficient token lookup)
   * @returns A new KaraokeTrack instance
   */
  static fromKaraokeDataAndTranscript(
    karaokeData: KaraokeData,
    transcript: TranscriptToken[],
  ): KaraokeTrack {
    const { clips, configs, defaultConfigId } = karaokeData;

    // Get default config or create a fallback
    const defaultConfig = defaultConfigId
      ? configs.get(defaultConfigId)
      : configs.values().next().value;

    if (!defaultConfig) {
      throw new Error('No karaoke configuration available');
    }

    // OPTIMIZATION: Create a token lookup map for O(1) access
    // For ~10,000 tokens, this is MUCH faster than repeated array lookups
    // Map creation: O(n), Lookup per clip: O(1) instead of O(n)
    // Total complexity: O(n + m) instead of O(n * m) where:
    //   n = number of tokens (~10,000)
    //   m = number of clips (~100-500)
    const tokenLookup = new Map<string, TranscriptToken>();
    for (let i = 0; i < transcript.length; i++) {
      const token = transcript[i];
      tokenLookup.set(token.id, token);
    }

    // Convert KaraokeLine[] to KaraokeClip[]
    const karaokeClips = clips.map(clip => {
      // Get config for this line, or use default
      const config = clip.karaokeConfigId
        ? configs.get(clip.karaokeConfigId) || defaultConfig
        : defaultConfig;

      // If tokens are already in the line, use them directly
      // Otherwise, look them up from the full transcript using the efficient map
      let tokensForClip: TranscriptToken[] = [];

      if (clip.transcriptTokenIds && clip.transcriptTokenIds.length > 0) {
        tokensForClip = clip.transcriptTokenIds
          .map(id => tokenLookup.get(id))
          .filter((token): token is TranscriptToken => token !== undefined);
      }

      if (tokensForClip.length !== clip.transcriptTokenIds.length) {
        console.warn(
          `Some tokens for karaoke clip ${clip.id} were not found in the transcript.`,
        );
      }

      return new KaraokeClip({
        id: clip.id,
        transcriptTokens: tokensForClip,
        hiddenTranscriptTokenIds: clip.hiddenTranscriptTokenIds || [],
        ignoredByFiltersIds: clip.ignoredByFiltersIds || [],
        startSecondsOffset: clip.startSecondsOffset,
        endSecondsOffset: clip.endSecondsOffset,
        karaokeConfig: config,
        track: KARAOKE_TRACK_NUMBER,
        translatedText: clip.translatedText,
        translatedPhrases: clip.translatedPhrases,
        phraseOriginalIndices: clip.phraseOriginalIndices,
        softBreakAfterTokenIds: clip.softBreakAfterTokenIds,
        softBreakPhrasePositions: clip.softBreakPhrasePositions,
      });
    });

    return new KaraokeTrack({
      id: String(KARAOKE_TRACK_NUMBER),
      type: 'text',
      karaokeClips,
      syntheticBreaks: karaokeData.syntheticBreaks ?? [],
    });
  }
}
